// 3.1 / 3.2 — the forms menu rows: every registered form with a live status and
// exactly one obvious action per state (design D4 — ambiguity is the enemy at
// 350px). The list is shared by the chat surface (inline under the pinned card
// or the collapsed Your Forms bar — design D3: it pushes content, no floating
// layer to trap) and the Home tab (3.3), which passes `onAction` to navigate
// back to chat after any action and `resumableOnly` to drop "Not started" rows
// (the chat menu keeps them — it is the browse surface).
//
// Statuses are always derived live from the shared values map, so a paused
// form's saved percentage is truthful by construction (design D1). Discarded
// forms are not listed: a discard's whole meaning is server-suppressed removal,
// and a menu row would quietly contradict it. A submitted form's Review action
// belongs to the corrections slice; until that slice lands its row carries the
// status alone (the same handler-less-guard posture as the pinned card's Open).
//
// Non-browsable (ambient) forms are never listed, in any lifecycle state —
// they are collection nets the bot fills from conversation, not tasks a
// visitor opens, and the design reference's "Your Forms" list contains only
// task forms (store tracker 6.30). A passive "in progress" status row for an
// ambient form the bot has partially filled was considered and deferred.
//
// Forms-corrections slice: a submitted row's Review action opens the overlay
// in its review variant (design D1) via `openReview` — independent of
// `activeFormId`, so opening review never disturbs whatever else is active or
// queued. The status line flips to "edits not sent" while the form is dirty
// (design D2).

import styles from './ConversationalForms.module.css';
import { MENU_STATUS_EDITS_NOT_SENT } from './constants';
import { useConversationalForms } from './FormsContext';
import { formLifecycle, isFormDirty, isFormTouched, isResumableForm, progressPct } from './formsSelectors';
import { CloseIcon } from './icons';

type FormsMenuRowsProps = {
    /** Called after any row action — the chat menu closes itself, the Home tab
     *  navigates to chat. */
    onAction?: () => void;
    /** Home tab: list only forms there is something to come back to (active,
     *  queued, submitted, idle with a saved value) — "Not started" rows stay
     *  in the chat menu, which is the browse surface. */
    resumableOnly?: boolean;
};

const FormsMenuRows = ({ onAction, resumableOnly = false }: FormsMenuRowsProps) => {
    const { registry, state, switchToForm, removeQueuedForm, openFormView, openReview } =
        useConversationalForms();
    if (!registry) return null;

    const rows = registry.forms.filter((f) =>
        resumableOnly
            ? isResumableForm(f, state)
            : f.browsable && formLifecycle(state, f.objectId) !== 'discarded'
    );
    if (!rows.length) return null;

    return (
        <ul className={styles.menuList} data-testid="cf-forms-menu">
            {rows.map((form) => {
                const lifecycle = formLifecycle(state, form.objectId);
                const pct = progressPct(form, state, registry);
                const saved = isFormTouched(form, state);

                let status: string;
                let actions: JSX.Element | null;
                switch (lifecycle) {
                    case 'active':
                        status = `In progress · ${pct}%`;
                        actions = (
                            <button
                                type="button"
                                className={styles.menuAction}
                                aria-label={`Open ${form.short}`}
                                onClick={() => {
                                    openFormView();
                                    onAction?.();
                                }}
                            >
                                Open
                            </button>
                        );
                        break;
                    case 'queued':
                        status = saved ? `Up next · saved ${pct}%` : 'Up next';
                        actions = (
                            <>
                                <button
                                    type="button"
                                    className={`${styles.menuAction} ${styles.menuActionPrimary}`}
                                    aria-label={`Switch to ${form.short}`}
                                    onClick={() => {
                                        switchToForm(form.objectId);
                                        onAction?.();
                                    }}
                                >
                                    Switch
                                </button>
                                <button
                                    type="button"
                                    className={styles.menuRemove}
                                    aria-label={`Remove ${form.short}`}
                                    onClick={() => removeQueuedForm(form.objectId)}
                                >
                                    <CloseIcon size={12} />
                                </button>
                            </>
                        );
                        break;
                    case 'submitted': {
                        const dirty = isFormDirty(state, form.objectId);
                        status = dirty ? MENU_STATUS_EDITS_NOT_SENT : 'Submitted';
                        actions = (
                            <button
                                type="button"
                                className={styles.menuAction}
                                aria-label={`Review ${form.short}`}
                                onClick={() => {
                                    openReview(form.objectId);
                                    onAction?.();
                                }}
                            >
                                Review
                            </button>
                        );
                        break;
                    }
                    default:
                        status = saved ? `Saved · ${pct}%` : 'Not started';
                        actions = (
                            <button
                                type="button"
                                className={styles.menuAction}
                                aria-label={`Start ${form.short}`}
                                onClick={() => {
                                    switchToForm(form.objectId);
                                    onAction?.();
                                }}
                            >
                                Start
                            </button>
                        );
                }

                return (
                    <li key={form.objectId} className={styles.menuRow} data-testid="cf-menu-row">
                        <span className={styles.menuText}>
                            <span className={styles.menuName}>{form.short}</span>
                            <span className={styles.menuStatus}>{status}</span>
                        </span>
                        {actions}
                    </li>
                );
            })}
        </ul>
    );
};

export default FormsMenuRows;
